import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ImagePlus } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import api, { mediaUrl } from "../../lib/api";
import { errorMessage } from "../../lib/stays";
import { useContent } from "../../context/SiteContentContext";

export default function AdminPayments({ property, onSaved }) {
  const content = useContent();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { if (property) setForm({ ...property }); }, [property]);
  if (!form) return <div className="text-white/40 py-10 text-center">Loading…</div>;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadQr = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Please choose an image under 5 MB"); return; }
    const fd = new FormData();
    fd.append("payment_qr", file);
    setUploading(true);
    try {
      const { data } = await api.patch(`/admin/properties/${property.id}`, fd);
      set("payment_qr", data.payment_qr);
      toast.success("Payment QR updated - guests see it right away");
      onSaved();
    } catch (err) {
      const d = err.response?.data;
      toast.error(d?.payment_qr ? [].concat(d.payment_qr).join(" ") : errorMessage(err, "Couldn't upload that image"));
    }
    setUploading(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/properties/${property.id}`, {
        payment_mode: form.payment_mode, qr_hold_minutes: Number(form.qr_hold_minutes) || 60,
        payment_upi_id: form.payment_upi_id || "", payment_whatsapp: form.payment_whatsapp || "",
        payment_hold_minutes: Number(form.payment_hold_minutes) || 15,
      });
      toast.success("Payment settings saved");
      onSaved();
    } catch (e) { toast.error(errorMessage(e, "Couldn't save")); }
    setBusy(false);
  };

  const box = "glass p-6 space-y-4";
  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-payments">
      <div className={box}>
        <div className="eyebrow">Payment QR code</div>
        <div className="flex flex-wrap items-start gap-6">
          {form.payment_qr
            ? <img src={mediaUrl(form.payment_qr)} alt="Current payment QR" className="w-40 h-auto border border-white/10" data-testid="admin-qr-preview" />
            : <div className="w-40 h-40 border border-white/10 flex items-center justify-center text-xs text-white/40 text-center p-3">No QR uploaded yet</div>}
          <div className="flex-1 min-w-[220px] space-y-3">
            <label className={`inline-flex items-center gap-2 px-4 py-3 border border-gold/50 text-gold text-xs uppercase tracking-[0.15em] cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : "hover:bg-gold/10"}`}>
              <ImagePlus size={15} /> {uploading ? "Uploading…" : form.payment_qr ? "Replace QR image" : "Upload QR image"}
              <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={uploadQr} aria-label="Upload payment QR" data-testid="qr-upload" />
            </label>
            <p className="text-xs text-white/40">Upload a screenshot or photo of your PhonePe / Google Pay / UPI QR. It replaces the old one immediately and is shown to guests after they reserve.</p>
          </div>
        </div>
      </div>

      <div className={box}>
        <div className="eyebrow">How guests pay</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Label className="text-xs text-white/50">Payment method</Label>
            <select value={form.payment_mode || "OFF"} onChange={(e) => set("payment_mode", e.target.value)} className="mt-1 w-full bg-[#0f0f0f] border border-white/10 h-10 px-2 text-sm" data-testid="payment-mode-select">
              <option value="QR">QR code + WhatsApp screenshot (you confirm each booking)</option>
              <option value="OFF">Pay at the property (bookings confirm instantly)</option>
              <option value="OPTIONAL">Razorpay or at property (gateway must be enabled on the server)</option>
              <option value="REQUIRED">Razorpay required (gateway must be enabled on the server)</option></select></div>
          <div><Label className="text-xs text-white/50">UPI ID (shown under the QR, optional)</Label>
            <Input value={form.payment_upi_id || ""} onChange={(e) => set("payment_upi_id", e.target.value)} placeholder="name@bank" className="mt-1 bg-[#0f0f0f] border-white/10 h-10" data-testid="upi-id-input" /></div>
          <div><Label className="text-xs text-white/50">WhatsApp number for screenshots</Label>
            <Input value={form.payment_whatsapp || ""} onChange={(e) => set("payment_whatsapp", e.target.value.replace(/[^\d]/g, ""))}
              placeholder={content.get("site.whatsapp")} className="mt-1 bg-[#0f0f0f] border-white/10 h-10" data-testid="whatsapp-input" />
            <p className="text-[11px] text-white/35 mt-1">Digits with country code. Leave empty to use your site WhatsApp number ({content.get("site.whatsapp")}).</p></div>
          <div><Label className="text-xs text-white/50">Dates held while the guest pays (minutes)</Label>
            <Input type="number" min="5" value={form.qr_hold_minutes ?? 60} onChange={(e) => set("qr_hold_minutes", e.target.value)} className="mt-1 bg-[#0f0f0f] border-white/10 h-10" /></div>
        </div>
        <p className="text-xs text-white/40">With QR payment, new bookings stay pending and hold the dates. When the screenshot arrives, open the booking and press “Payment received – confirm booking”.</p>
        <button type="button" onClick={save} disabled={busy} className="btn-gold disabled:opacity-40" data-testid="save-payments">{busy ? "Saving…" : "Save payment settings"}</button>
      </div>
    </div>
  );
}
